import { jsxs, jsx } from "react/jsx-runtime";
import { ApolloClient, InMemoryCache, HttpLink } from "@apollo/client";
import { Tabs } from "@zillow/constellation";
import { SearchBox, buildURL } from "@zillow/search-box";
import { event } from "@zillow/universal-analytics";
import PropTypes from "prop-types";
import React__default, { useState, useRef, useMemo } from "react";
import styled from "styled-components";
import triggerTabClickEvent from "../../utils/clickstream/tab-click.esm.js";
const CategoryTabList = styled(Tabs.List).withConfig({
  displayName: "PropertySearch__CategoryTabList",
  componentId: "sc-shrp8h-0"
})(["border-bottom-color:transparent;"]);
const CategoryTab = styled(Tabs.Tab).withConfig({
  displayName: "PropertySearch__CategoryTab",
  componentId: "sc-shrp8h-1"
})(["background-color:transparent !important;color:inherit !important;&[aria-selected='false']:before{content:unset !important;}"]);
function getAction(category, origin, searchSelection, location) {
  var _a, _b;
  switch (category) {
    case "buy":
      const buyPath = buildURL(searchSelection, location, "for_sale");
      return `${origin}${buyPath}`;
    case "rent":
      const rentPath = buildURL(searchSelection, location, "for_rent");
      return `${origin}${rentPath}`;
    case "sell":
      if (!((_a = searchSelection == null ? void 0 : searchSelection.data) == null ? void 0 : _a.zpid)) {
        throw new Error(`Could not find zpid for sell category`);
      }
      return `${origin}/sell/options/address-verification/?zpid=${searchSelection.data.zpid}&source=${window.location.origin}${window.location.pathname}`;
    case "zestimate":
      if (!((_b = searchSelection == null ? void 0 : searchSelection.data) == null ? void 0 : _b.zpid)) {
        throw new Error(`Could not find zpid for zestimate category`);
      }
      return `${origin}/myzillow/yourhome/${searchSelection.data.zpid}`;
    default:
      throw new Error(`Unknown category of "${category}"`);
  }
}
const createSearchBoxApolloClient = (uri = "https://zg-graph-dev.int.zgcp-federated-nonprod-k8s.zg-int.net/graphql/") => {
  return new ApolloClient({
    ssrMode: typeof window === "undefined",
    link: new HttpLink({
      uri,
      credentials: "same-origin",
      headers: {
        "x-caller-id": "zrs-cms"
      }
    }),
    cache: new InMemoryCache()
  });
};
const PropertySearch = ({
  propertySearchConfigValues: {
    origin,
    graphURL
  },
  placeholder,
  categories = ["buy", "rent", "sell", "zestimate"],
  size,
  id,
  t,
  opensInNewWindow
}) => {
  const [category, setCategory] = useState(categories[0]);
  const inputRef = useRef(null);
  const zgGraphClient = useMemo(() => createSearchBoxApolloClient(graphURL), [graphURL]);
  const boxProps = {
    abTests: {
      SXP_SEARCH_BOX_URL_ADDRESS: "BDP_HDP_SRP"
    },
    inputRef,
    handleSubmit: (value, location) => {
      const action = getAction(category, origin, value, location);
      const url = new URL(action);
      if (t) {
        url.searchParams.append("t", t);
      }
      window.open(url.href, opensInNewWindow ? "_blank" : "_self");
      event({
        // https://dataportal.zillowgroup.net/event-registry/2055
        envelope: {
          event_type_id: "2055",
          event_type_version_id: "1",
          event_template_id: "126",
          event_template_version_id: "1"
          // Master ID: 2055.1.126.1
        },
        clickstream_trigger: {
          trigger_type_nm: "interaction",
          trigger_location_nm: "cms",
          trigger_source_nm: "cms_button",
          trigger_object_nm: "no_trigger_object"
        },
        semantic: {
          semantic_event_nm: "cms_submit"
        },
        cms_info: {
          block_id: id || "",
          block_type_cd: "zg_property_search",
          block_target_url: action,
          block_displayed_txt: placeholder,
          form_business_line_txt: category,
          form_submit_label: "magnifying glass icon"
        }
      });
    },
    recentSearchLocalStorageKey: "recentSearchesEnhanced",
    fetchSavedHomes: true,
    fetchSavedSearches: true,
    platform: "CCMS",
    filterSuggestions: ["sell", "zestimate"].includes(category) ? (suggestions) => suggestions.filter((suggestion) => suggestion.category === "ADDRESS") : void 0,
    platformConfig: {
      CCMS: {
        placeholderText: placeholder,
        supportsLocationIcon: false
      }
    },
    size,
    // Only suggest addresses if sell or zestimate is selected (don't suggest regions)
    resultTypes: ["sell", "zestimate"].includes(category) ? ["ADDRESSES"] : ["REGIONS", "FORSALE", "RENTALS", "SOLD"],
    zgGraphClient
  };
  const categoryOptions = [{
    value: "buy",
    label: "Buy"
  }, {
    value: "rent",
    label: "Rent"
  }, {
    value: "sell",
    label: "Sell"
  }, {
    value: "zestimate",
    label: "Zestimate"
  }].filter((categoryOption) => categories.includes(categoryOption.value));
  return /* @__PURE__ */ jsxs(React__default.Fragment, { children: [
    categoryOptions.length > 1 && /* @__PURE__ */ jsx(CategoryTabList, { id, "aria-label": "Property Search Categories", defaultSelected: categoryOptions[0].value, onSelected: (value) => {
      setCategory(value);
      const categoryOption = categoryOptions.find((option) => option.value === value);
      if (!categoryOption) {
        throw new Error(`Could not find category option for value of "${value}"`);
      }
      triggerTabClickEvent({
        label: categoryOption.label,
        id: `${id}-${value}`
      });
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        // eslint-disable-next-line no-undef
        window.HTMLInputElement.prototype,
        "value"
      ).set;
      if (!inputRef.current) {
        return;
      }
      nativeInputValueSetter.call(inputRef.current, "");
      const inputEvent = new Event("input", {
        bubbles: true
      });
      inputRef.current.dispatchEvent(inputEvent);
    }, children: categoryOptions.map(({
      value,
      label
    }) => /* @__PURE__ */ jsx(CategoryTab, { id: value, children: label }, value)) }),
    /* @__PURE__ */ jsx(SearchBox, { ...boxProps })
  ] });
};
PropertySearch.propTypes = {
  propertySearchConfigValues: PropTypes.shape({
    graphURL: PropTypes.string,
    origin: PropTypes.string
  }).isRequired,
  placeholder: PropTypes.string,
  // Must have at least one
  categories: PropTypes.arrayOf(PropTypes.oneOf(["buy", "rent", "sell", "zestimate"])).isRequired,
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  id: PropTypes.string.isRequired,
  t: PropTypes.string,
  opensInNewWindow: PropTypes.bool
};
PropertySearch.defaultProps = {
  placeholder: "Enter an address, neighborhood, city, or ZIP code",
  size: "md",
  t: "",
  opensInNewWindow: true
};
export {
  PropertySearch as default
};
